import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, ArrowRight, CalendarDays, Check, CheckCircle2, ChevronRight, Clock, GripVertical, Lock, PlayCircle,
  Search, Star, StickyNote, Target, Grid, Video, Phone, Plus, Eye, Sparkles, Users,
  Percent, DollarSign, TrendingUp, Wallet, ArrowLeftRight, ArrowDownToLine, ArrowUpFromLine, Award, Settings, Share2, Megaphone, Mail, Book, Filter,
  ZoomIn, ZoomOut, Maximize2,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { CONTACT_TO_BUILD_STEPS, CONTACT_TO_BUILD_GOAL } from '../../data/contactToBuildDefault';
import { DEFAULT_HR_SECTIONS } from '../../utils/hrMaterials';
import { DEPARTMENT_SECTIONS } from '../KickoffAssessmentSections';
import { FlowPreviewShell } from './StartFlowPreview';

/**
 * Dummy, look-don't-touch mimics of the client portal for the "Our Process"
 * section: Kickoff (/portal/kickoff), Roadmap (/portal/roadmap) and Scale
 * (the fully unlocked /portal dashboard + a department hub). Markup mirrors
 * PortalKickoff + PortalKickoffPriorities, Roadmap.js, ClientDashboard.js and
 * PortalOperations.js — keep them in step.
 */

const PORTAL_BG = 'min-h-full bg-[#f4f2ef] dark:bg-[#17140f]';
const CARD = 'rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm';
const PANEL = 'rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/85 dark:bg-gray-800/85 backdrop-blur-sm shadow-sm';

const DEPT_PILL_SOLID = {
  sales: 'bg-red-500 text-white',
  operations: 'bg-blue-500 text-white',
  technology: 'bg-purple-500 text-white',
  finance: 'bg-green-500 text-white',
  marketing: 'bg-orange-500 text-white',
  hr: 'bg-yellow-500 text-white',
};
const DEPT_PILL_SOFT = {
  sales: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  operations: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  finance: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
  technology: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  marketing: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  hr: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  onboarding: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
};
const deptName = (id) => ({ sales: 'Sales', operations: 'Operations', finance: 'Finance', technology: 'Technology', marketing: 'Marketing', hr: 'HR', onboarding: 'Onboarding' }[id] || id);

// Dummy priority levels per department (0 low · 1 medium · 2 top), indexed like DEPARTMENT_SECTIONS items.
const DUMMY_LEVELS = {
  sales: [2, 1, 0, 0],
  operations: [1, 2, 0, 0],
  technology: [0, 0, 2, 1],
  finance: [2, 0, 1, 0],
  marketing: [0, 2, 0, 1],
  hr: [0, 1, 0, 0],
};
const DUMMY_NOTES = {
  'sales-0': 'We need a repeatable handoff from sales to production.',
  'finance-0': 'Job costing is the one that keeps me up at night.',
};

// Dummy roadmap for a residential contractor.
const ROADMAP = [
  { id: 'r1', emoji: '🚀', title: 'Kickoff & Priorities', department: 'onboarding', status: 'completed', description: 'Set your top priorities, complete your profile, and tour the portal.' },
  { id: 'r2', emoji: '📝', title: 'Sales Process & Proposal Templates', department: 'sales', status: 'completed', description: 'Lock in a repeatable sales process from first call to signed contract.' },
  { id: 'r3', emoji: '⚙️', title: 'SOP Implementation', department: 'operations', status: 'in-progress', description: 'Document and roll out the standard operating procedures your team runs on every job.',
    tasks: [
      { title: 'Watch: Building SOPs That Stick', description: 'A 20-minute course on writing procedures your crew will actually follow.', done: true },
      { title: 'Pick your first three SOPs', description: 'Start with change orders, daily job logs, and client walkthroughs. Your success manager reviews your picks on the next call.', done: false },
      { title: 'Tailor the SOP templates', description: 'Open each template in Operations ▸ SOPs and tailor it to how your company already works.', done: false },
      { title: 'Roll out to the team', description: 'Share the SOPs with your leads and schedule a 30-minute walkthrough.', done: false },
    ] },
  { id: 'r4', emoji: '💵', title: 'Job Costing Setup', department: 'finance', status: 'locked', description: 'Track every job against its estimate so margin stops being a surprise.' },
  { id: 'r5', emoji: '🤖', title: 'JobTread Automations', department: 'technology', status: 'locked', description: 'Automate follow-ups, schedules, and selections inside JobTread.' },
  { id: 'r6', emoji: '🔔', title: 'Lead Generation System', department: 'marketing', status: 'locked', description: 'Build a predictable pipeline from referrals, reviews, and digital presence.' },
  { id: 'r7', emoji: '📋', title: 'Roles & Job Descriptions', department: 'hr', status: 'locked', description: 'Define who owns what so you can hire ahead of growth.' },
  { id: 'r8', emoji: '🎯', title: 'KPI Dashboard', department: 'operations', status: 'locked', description: 'Track the handful of numbers that tell you the business is healthy.' },
  { id: 'r9', emoji: '💰', title: 'Pricing & Markup Strategy', department: 'finance', status: 'locked', description: 'Set markup by project type so every bid protects margin.' },
  { id: 'r10', emoji: '🌐', title: 'Website & Digital Presence', department: 'marketing', status: 'locked', description: 'Turn the website into a lead machine with reviews and clear service pages.' },
  { id: 'r11', emoji: '💬', title: 'Client Communication System', department: 'operations', status: 'locked', description: 'Weekly updates, selections deadlines, and a project hub every client can trust.' },
  { id: 'r12', emoji: '👥', title: 'Hiring Your First Project Manager', department: 'hr', status: 'locked', description: 'Job description, compensation plan, and a 90-day onboarding path.' },
  { id: 'r13', emoji: '🖥️', title: 'Tech Stack Cleanup', department: 'technology', status: 'locked', description: 'Retire duplicate tools and connect what is left through open APIs.' },
  { id: 'r14', emoji: '🤝', title: 'Referral Partner Program', department: 'sales', status: 'locked', description: 'Realtors, designers, and suppliers sending you qualified leads.' },
  { id: 'r15', emoji: '📊', title: 'Cash Flow Forecasting', department: 'finance', status: 'locked', description: 'See draws, payables, and overhead twelve weeks out.' },
  { id: 'r16', emoji: '🏁', title: 'Quarterly Business Review', department: 'operations', status: 'locked', description: 'Score the quarter against goals and reset the roadmap for the next one.' },
];

const fmtDate = (d) => d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
const nextWeekday = (dow) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  do {
    d.setDate(d.getDate() + 1);
  } while (d.getDay() !== dow);
  return d;
};

/* ---------------- Kickoff (/portal/kickoff) ---------------- */

const KickoffScreen = () => {
  const topPriorities = [];
  DEPARTMENT_SECTIONS.forEach((sec) => {
    (sec.items || []).forEach((item, i) => {
      if ((DUMMY_LEVELS[sec.id] || [])[i] === 2) {
        topPriorities.push({ key: `${sec.id}-${i}`, emoji: item.emoji, label: item.label, deptId: sec.id, deptName: sec.name, note: DUMMY_NOTES[`${sec.id}-${i}`] || '' });
      }
    });
  });
  const covered = new Set(topPriorities.map((t) => t.deptId));

  return (
    <div className={`${PORTAL_BG} p-6 text-gray-900 dark:text-gray-100`}>
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="relative text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-3">
            <span className="text-4xl leading-none" aria-hidden="true">🎯</span>
          </div>
          <h1 className="text-3xl font-bold text-center">Top Priorities</h1>
          <p className="mt-2 text-center max-w-xl mx-auto text-gray-500 dark:text-gray-400">
            Tell us what matters most in your company — tap the cards to rank each area, then drag your top picks into order. Everything saves as you go.
          </p>
        </header>

        {/* Department jump chips */}
        <div className="flex flex-wrap justify-center gap-2">
          {DEPARTMENT_SECTIONS.map((sec) => {
            const isCovered = covered.has(sec.id);
            return (
              <span
                key={sec.id}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium backdrop-blur-sm ${
                  isCovered
                    ? 'bg-amber-50/90 border-amber-300 text-amber-800 dark:bg-amber-900/40 dark:border-amber-600 dark:text-amber-200'
                    : 'bg-white/75 border-gray-200 text-gray-600 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-300'
                }`}
              >
                <span className="text-sm leading-none">{sec.emoji}</span>
                {sec.name}
                {isCovered && <Star className="w-3 h-3 fill-amber-500 text-amber-500" />}
              </span>
            );
          })}
        </div>

        {/* Ranked Top Priorities */}
        <section className={`${PANEL} p-5 sm:p-6`}>
          <header className="text-center mb-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center mb-2">
              <Star className="w-6 h-6 fill-amber-500 text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-center">Your Top Priorities</h2>
            <p className="mt-1 text-sm text-center text-gray-500 dark:text-gray-400">Drag to put them in order — #1 is where we start.</p>
            <div className="mt-3 mx-auto max-w-xs">
              <div className="flex gap-1">
                {DEPARTMENT_SECTIONS.map((sec) => (
                  <div key={sec.id} className={`h-1.5 flex-1 rounded-full ${covered.has(sec.id) ? 'bg-amber-400' : 'bg-gray-200 dark:bg-gray-700'}`} />
                ))}
              </div>
              <p className="mt-1.5 text-[11px] text-center text-gray-500 dark:text-gray-400">
                {covered.size} of {DEPARTMENT_SECTIONS.length} departments covered
              </p>
            </div>
          </header>
          <ol className="space-y-2 max-w-2xl mx-auto">
            {topPriorities.map((row, idx) => (
              <li key={row.key} className="rounded-xl border px-3 py-2.5 bg-white border-gray-200 dark:bg-gray-900 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                <div className="flex items-center gap-2.5">
                  <GripVertical className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${idx < 3 ? 'bg-gradient-to-br from-amber-400 to-amber-500 text-white shadow-sm' : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-100'}`}>
                    {idx + 1}
                  </span>
                  <span className="text-lg leading-none shrink-0">{row.emoji}</span>
                  <span className="flex-1 text-sm font-medium truncate text-gray-900 dark:text-gray-100">{row.label}</span>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium ${DEPT_PILL_SOLID[row.deptId] || 'bg-gray-500 text-white'}`}>{row.deptName}</span>
                </div>
                <div className="mt-1 pl-[62px] pr-2">
                  {row.note ? (
                    <p className="flex items-start gap-1.5 text-xs italic text-gray-500 dark:text-gray-400">
                      <StickyNote className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>{row.note}</span>
                    </p>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400"><StickyNote className="w-3 h-3" /> Add note</span>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Department sections */}
        {DEPARTMENT_SECTIONS.map((sec) => (
          <section key={sec.id} className={`${PANEL} p-5 sm:p-6`}>
            <header className="text-center mb-4">
              <div className="flex items-center justify-center gap-3">
                <span className="h-px flex-1 max-w-[110px] bg-gray-200 dark:bg-gray-700" />
                <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                  <span className="text-xl leading-none">{sec.emoji}</span>
                </span>
                <h3 className="text-xl md:text-2xl font-bold tracking-tight leading-tight text-center">{sec.name}</h3>
                <span className="h-px flex-1 max-w-[110px] bg-gray-200 dark:bg-gray-700" />
              </div>
              <p className="mt-1.5 text-xs text-center text-gray-500 dark:text-gray-400">Tap a card to raise its priority — Low, Medium, then gold Top Priority.</p>
            </header>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {(sec.items || []).map((item, i) => {
                const level = (DUMMY_LEVELS[sec.id] || [])[i] || 0;
                const isTop = level === 2;
                const shell = isTop
                  ? 'bg-gradient-to-b from-amber-50 to-amber-100/80 border-amber-300 shadow-md shadow-amber-200/40 dark:from-amber-900/40 dark:to-amber-900/20 dark:border-amber-600'
                  : level === 1
                    ? 'border-amber-300/70 dark:border-amber-700/70 bg-white/85 dark:bg-gray-800/85'
                    : 'bg-white/75 border-gray-200 dark:bg-gray-800/75 dark:border-gray-700';
                return (
                  <div key={i} className={`relative rounded-xl border p-3 min-h-[128px] flex flex-col items-center justify-between text-center backdrop-blur-sm ${shell}`}>
                    {isTop && (
                      <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 shadow flex items-center justify-center">
                        <Star className="w-3.5 h-3.5 fill-white text-white" />
                      </span>
                    )}
                    <span className="text-2xl mt-2 leading-none">{item.emoji}</span>
                    <span className="text-sm font-medium leading-snug">{item.label}</span>
                    <div className="w-full max-w-[104px]">
                      <div className="flex gap-1">
                        {[0, 1, 2].map((l) => (
                          <span key={l} className={`h-1.5 flex-1 rounded-full ${level >= l ? 'bg-amber-400' : 'bg-gray-300 dark:bg-gray-700'}`} />
                        ))}
                      </div>
                      <p className={`mt-1 text-[11px] font-medium ${isTop ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        {isTop ? '⭐ Top Priority' : level === 1 ? 'Medium priority' : 'Low priority'}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div className="rounded-xl border-2 border-dashed p-3 min-h-[128px] flex flex-col items-center justify-center gap-1.5 border-gray-300 text-gray-500 dark:border-gray-600 dark:text-gray-400">
                <Plus className="w-5 h-5" />
                <span className="text-sm font-medium">Add your own</span>
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Roadmap (/portal/roadmap) ---------------- */

const RoadmapTabs = ({ active }) => (
  <div className="flex items-center justify-center gap-2 mb-4">
    {[
      ['focus', 'Current Focus', Target],
      ['overview', 'Overview', Grid],
    ].map(([key, label, Icon]) => (
      <span
        key={key}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
          active === key ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
        }`}
      >
        <Icon className="w-3.5 h-3.5" />
        {label}
      </span>
    ))}
  </div>
);

const RoadmapHeader = () => (
  <div className="relative mb-6">
    <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
      <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
        <ArrowLeft className="h-5 w-5" />
      </span>
    </div>
    <div className="text-center px-32 py-1">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Roadmap</h1>
    </div>
  </div>
);

const CompactStep = ({ item }) => {
  const done = item.status === 'completed';
  return (
    <div className={`rounded-xl border shadow-sm p-4 ${done ? 'border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-900/20' : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'}`}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold flex items-center gap-2 text-base text-gray-900 dark:text-white">
          <span>{item.emoji}</span>
          <span className="truncate">{item.title}</span>
        </h3>
        {done ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 shrink-0">
            <CheckCircle2 className="w-3 h-3" />
            Complete
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 shrink-0">
            <Lock className="w-3 h-3" />
            Upcoming
          </span>
        )}
      </div>
    </div>
  );
};

const RoadmapFocusScreen = () => {
  const current = ROADMAP.find((r) => r.status === 'in-progress');
  const currentTask = 1;
  return (
    <div className={`${PORTAL_BG} p-4`}>
      <div className="max-w-7xl mx-auto p-2">
        <RoadmapHeader />
        <RoadmapTabs active="focus" />
        <div className="space-y-3">
          {ROADMAP.filter((r) => r.status === 'completed').map((r) => <CompactStep key={r.id} item={r} />)}

          {/* Current step: giant stepper + sidebar */}
          <div className="w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm my-2">
            <div className="p-5 w-full flex gap-6">
              <div className="w-2/3">
                <div className="text-center mb-3">
                  <div className="mb-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${DEPT_PILL_SOFT[current.department]}`}>{deptName(current.department)}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-2xl">{current.emoji}</span>
                    <h3 className="font-bold text-2xl text-gray-900 dark:text-white">{current.title}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{current.description}</p>
                </div>
                <div className="w-full max-w-3xl mx-auto">
                  {/* stepper circles */}
                  <div className="flex items-center justify-center gap-2 px-4 pt-2">
                    {current.tasks.map((t, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span className={`h-0.5 w-10 sm:w-16 rounded ${i <= currentTask ? 'bg-red-600' : 'bg-gray-200 dark:bg-gray-700'}`} />}
                        <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                          i < currentTask ? 'bg-red-600 text-white' : i === currentTask ? 'bg-red-600 text-white ring-4 ring-red-100 dark:ring-red-900/40' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}>
                          {i < currentTask ? <Check className="w-4 h-4" /> : i + 1}
                        </span>
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="p-4 min-h-[200px]">
                    <div className="py-3">
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2 text-center">{current.tasks[currentTask].title}</h2>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">{current.tasks[currentTask].description}</p>
                      <div className="mt-3">
                        <span className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold shadow-sm">
                          Open Operations ▸ SOPs
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-4 pb-2">
                    <span className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800">Previous</span>
                    <span className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600">Next</span>
                  </div>
                </div>
              </div>
              <div className="w-1/3 flex flex-col gap-3">
                {[
                  ['💬', 'bg-blue-50 dark:bg-blue-900/30', 'Ask a Question', 'Get help with this step'],
                  ['📞', 'bg-emerald-50 dark:bg-emerald-900/30', 'Schedule a Call', 'Book time with your advisor'],
                  ['➕', 'bg-purple-50 dark:bg-purple-900/30', 'Create a Task', 'Add a custom task'],
                ].map(([emoji, tile, title, sub]) => (
                  <div key={title} className="flex-1 p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col items-center justify-center text-center">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${tile} mb-2`}>
                      <span className="text-xl leading-none">{emoji}</span>
                    </div>
                    <div className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">{title}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {ROADMAP.filter((r) => r.status === 'locked').slice(0, 3).map((r) => <CompactStep key={r.id} item={r} />)}
        </div>
      </div>
    </div>
  );
};

const RoadmapOverviewScreen = () => (
  <div className={`${PORTAL_BG} p-4`}>
    <div className="max-w-7xl mx-auto p-2">
      <RoadmapHeader />
      <RoadmapTabs active="overview" />
      <div className="flex flex-row items-center justify-center gap-2 mb-4 px-4">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <div className="w-full pl-9 pr-9 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-400">Search your roadmap...</div>
        </div>
        <div className="w-48 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100">All Departments</div>
      </div>
      <div className="p-4 grid grid-cols-3 gap-4">
        {ROADMAP.map((item, i) => {
          const active = item.status === 'in-progress';
          const done = item.status === 'completed';
          const colors = active
            ? 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20'
            : done
              ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20'
              : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/50';
          return (
            <div key={item.id} className={`rounded-xl border p-4 flex flex-col items-center text-center gap-2 ${colors}`}>
              <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500">Step {i + 1}</span>
              <span className="w-10 h-10 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-xl">{item.emoji}</span>
              <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">{item.title}</p>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${DEPT_PILL_SOFT[item.department]}`}>{deptName(item.department)}</span>
              {active ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"><PlayCircle className="w-3 h-3" />In Progress</span>
              ) : done ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"><CheckCircle2 className="w-3 h-3" />Complete</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"><Lock className="w-3 h-3" />Upcoming</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

/* ---------------- Scale: dashboard + department hub ---------------- */

const SectionTitle = ({ emoji, children, right }) => (
  <div className="flex items-center justify-between gap-2 mb-4">
    <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
      <span className="text-base leading-none" aria-hidden="true">{emoji}</span>
      {children}
    </h2>
    {right}
  </div>
);

const Pill = ({ children }) => (
  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">{children}</span>
);

const WHATS_NEW = [
  ['📖', 'New SOP published', 'Change Order SOP is ready to tailor', 'Today'],
  ['🎓', 'New lesson', 'Pricing for Profit · Lesson 4: Markup vs Margin', 'Today'],
  ['✅', 'Task completed', 'Upload last 12 months of P&L', 'Yesterday'],
  ['🗺️', 'Roadmap note', 'Your success manager left a note on SOP Implementation', 'Yesterday'],
  ['📞', 'Call scheduled', 'Strategy Call · SOP rollout', '2 days ago'],
  ['💬', 'Question answered', 'How should we handle allowances on change orders?', '3 days ago'],
  ['📂', 'New material', 'Realtor Partner Guide added to Sales materials', '4 days ago'],
  ['🎥', 'Breakthrough Session', 'Recording posted: Hiring Your First Project Manager', '5 days ago'],
];
const OPS_WHATS_NEW = [
  ['📖', 'New SOP published', 'Change Order SOP is ready to tailor', 'Today'],
  ['✨', 'SOP tailored', 'Photo & Job Logs SOP saved with your edits', 'Today'],
  ['📈', 'KPI updated', 'Gross margin per job now tracking from JobTread', 'Yesterday'],
  ['💼', 'Job description', 'Project Manager job description tailored', 'Yesterday'],
  ['🏢', 'Org chart', 'Estimator added as planned hire #1', '2 days ago'],
  ['✅', 'Task completed', 'Pick your first three SOPs', '3 days ago'],
  ['🗺️', 'Roadmap note', 'Your success manager left a note on SOP Implementation', '3 days ago'],
  ['📞', 'Call scheduled', 'Strategy Call · SOP rollout', '4 days ago'],
];

const WhatsNewCard = ({ className = '', items = WHATS_NEW }) => (
  <div className={`${CARD} flex flex-col ${className}`}>
    <div className="flex flex-wrap items-center justify-between gap-3 p-5 pb-4">
      <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
        <span className="text-base leading-none" aria-hidden="true">✨</span>
        What's New
      </h2>
    </div>
    <div className="flex-1 px-3 pb-3 space-y-1">
      {items.map(([emoji, kind, text, when]) => (
        <div key={text} className="flex items-center gap-3 p-2.5 rounded-lg">
          <span className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 flex items-center justify-center text-lg shrink-0">{emoji}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{text}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{kind}</p>
          </div>
          <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">{when}</span>
        </div>
      ))}
    </div>
  </div>
);
const ImplementationHoursCard = ({ className = '' }) => {
  const call = nextWeekday(2);
  const call2 = nextWeekday(4);
  return (
    <div className={`${CARD} p-5 flex flex-col ${className}`}>
      <SectionTitle emoji="📞" right={<Pill>Resets in 18d</Pill>}>Implementation Hours</SectionTitle>
      <div className="flex flex-col items-center text-center py-1">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-5xl font-bold text-gray-900 dark:text-white leading-none">3</span>
          <span className="text-2xl font-semibold text-gray-400 dark:text-gray-500 leading-none">/ 4</span>
        </div>
        <p className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-300">Hours remaining this period</p>
        <div className="mt-3 w-full max-w-[220px] h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-red-500 to-rose-500" style={{ width: '25%' }} />
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Upcoming Calls</p>
        <div className="space-y-1">
          {[
            ['Strategy Call · SOP rollout', call],
            ['Finance check-in', call2],
          ].map(([title, when]) => (
            <div key={title} className="flex items-start gap-2.5 rounded-lg px-2 py-1.5">
              <span className="mt-1.5 w-2 h-2 rounded-full shrink-0 bg-red-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{title}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{when.toLocaleDateString([], { month: 'short', day: 'numeric' })} at 10:00 AM</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <span className="mt-4 w-full py-2 px-4 rounded-lg text-sm font-semibold bg-red-600 text-white text-center">Add More Hours</span>
      <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center text-xs font-semibold shrink-0">IR</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">Ian Riddle</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Your Success Manager</p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
          <Phone className="w-3.5 h-3.5" />
          419-690-9665
        </span>
      </div>
    </div>
  );
};

const RoadmapCard = ({ className = '' }) => {
  const done = ROADMAP.filter((r) => r.status === 'completed').length;
  const pct = Math.round((done / ROADMAP.length) * 100);
  const current = ROADMAP.find((r) => r.status === 'in-progress');
  const idx = ROADMAP.indexOf(current);
  return (
    <div className={`${CARD} p-6 flex flex-col ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <span className="text-base leading-none" aria-hidden="true">🗺️</span>
          Your Roadmap
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{done} of {ROADMAP.length} milestones complete</span>
          <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-gray-900 inline-flex items-center gap-1">
            View Full Roadmap
            <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
      <div className="mb-5 flex items-center gap-3">
        <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 w-10 text-right">{pct}%</span>
      </div>
      <div className="flex items-center gap-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4">
        <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-red-600 text-white ring-4 ring-red-100 dark:ring-red-900/40">
          <span className="text-lg font-bold">{idx + 1}</span>
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400">Step {idx + 1} of {ROADMAP.length} · In Progress</p>
          <p className="text-base font-semibold text-gray-900 dark:text-white truncate">{current.title}</p>
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Up next · {current.title}</p>
        <div className="flex flex-wrap gap-2">
          {current.tasks.filter((t) => !t.done).slice(0, 3).map((t) => (
            <span key={t.title} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
              {t.title}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const GOALS = [
  ['Close Ratio', 38, 45, '%', '#10B981'],
  ['Gross Profit Margin', 34, 40, '%', '#3B82F6'],
  ['Monthly Leads', 22, 30, '', '#F59E0B'],
];

const GoalsCard = ({ className = '' }) => (
  <div className={`${CARD} p-5 flex flex-col ${className}`}>
    <SectionTitle emoji="🎯" right={<span className="text-lg">📌</span>}>Goal Tracking</SectionTitle>
    <div className="flex-1 space-y-4">
      {GOALS.map(([name, cur, target, unit, color]) => (
        <div key={name}>
          <div className="flex items-baseline justify-between gap-2 mb-1.5">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{name}</span>
            <span className="text-xs font-semibold text-gray-900 dark:text-white shrink-0">
              {cur}{unit} <span className="text-gray-400 dark:text-gray-500 font-medium">/ {target}{unit}</span>
            </span>
          </div>
          <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.round((cur / target) * 100)}%`, backgroundColor: color }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TASKS = [
  ['Pick your first three SOPs', 'Roadmap · SOP Implementation', false],
  ['Review the proposal template draft', 'Due in 2 days', false],
  ['Send updated org chart to your team', 'Due next week', false],
];

const TasksCard = ({ className = '' }) => (
  <div className={`${CARD} p-5 flex flex-col ${className}`}>
    <SectionTitle emoji="✅" right={<Pill>{TASKS.length} open</Pill>}>My Tasks</SectionTitle>
    <div className="flex-1 space-y-1.5">
      {TASKS.map(([title, sub, roadmap]) => (
        <div key={title} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2">
          <span className="mt-0.5 w-5 h-5 rounded-full border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate text-gray-800 dark:text-gray-100">{title}</p>
            <p className={`text-xs ${sub.startsWith('Roadmap') ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>{sub}</p>
          </div>
        </div>
      ))}
    </div>
    <span className="mt-4 w-full py-2 px-4 rounded-lg text-sm font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-center">View All Tasks</span>
  </div>
);

const CommunityCard = ({ className = '' }) => (
  <div className={`${CARD} p-5 flex flex-col ${className}`}>
    <SectionTitle emoji="💬">Community</SectionTitle>
    <div className="flex-1 space-y-3">
      {[
        ['MK', 'Mike K.', 'Anyone using JobTread for selections sign-off? Curious how you handle change orders mid-job.', '4 replies'],
        ['SR', 'Sarah R.', 'Closed our biggest remodel yet this week using the new proposal template 🎉', '12 reactions'],
      ].map(([initials, name, text, meta]) => (
        <div key={name} className="flex items-start gap-3">
          <span className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center text-[11px] font-semibold shrink-0">{initials}</span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{name}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-snug">{text}</p>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{meta}</p>
          </div>
        </div>
      ))}
    </div>
    <span className="mt-4 w-full py-2 px-4 rounded-lg text-sm font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-center">Open Community</span>
  </div>
);

const BreakthroughHero = ({ className = '' }) => {
  const session = nextWeekday(4);
  return (
    <div className={`relative overflow-hidden rounded-xl text-white shadow-sm ${className}`} style={{ background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 55%, #f97316 100%)' }}>
      <div className="relative z-10 p-7 flex flex-col h-full">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/70">
          <Video className="w-4 h-4" />
          <span>Next Breakthrough Session</span>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-6 flex-1">
          <div className="flex-1 min-w-[220px]">
            <h2 className="text-2xl font-bold leading-snug">Pricing for Profit: Building Margin Into Every Bid</h2>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/85">
              <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{session.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />12:00 PM ET</span>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white/20 ring-2 ring-white/40 flex items-center justify-center text-sm font-semibold">IR</div>
              <div>
                <p className="text-xs text-white/70">Presented by</p>
                <p className="text-sm font-semibold">Ian Riddle</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2">
              {[['3', 'Days'], ['14', 'Hrs'], ['22', 'Min']].map(([v, u]) => (
                <div key={u} className="w-14 rounded-lg bg-white/15 backdrop-blur-sm py-2 text-center">
                  <p className="text-xl font-bold leading-none">{v}</p>
                  <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-white/70">{u}</p>
                </div>
              ))}
            </div>
            <span className="w-full px-5 py-2.5 rounded-lg bg-white text-sm font-bold text-center shadow" style={{ color: '#dc2626' }}>Join Session</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardScreen = ({ firstName }) => {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const metrics = [
    ['📣', '2', 'Announcements', 'bg-blue-50 dark:bg-blue-900/30', 'text-blue-600 dark:text-blue-400'],
    ['❓', '1', 'Open Questions', 'bg-purple-50 dark:bg-purple-900/30', 'text-purple-600 dark:text-purple-400'],
    ['🔥', '0', 'Active Fires', 'bg-red-50 dark:bg-red-900/30', 'text-gray-300 dark:text-gray-600'],
    ['✉️', '3', 'Unread Messages', 'bg-indigo-50 dark:bg-indigo-900/30', 'text-indigo-600 dark:text-indigo-400'],
  ];
  return (
    <div className={`${PORTAL_BG} p-6`}>
      <div className="relative">
        <div className="relative mb-6 text-center">
          <span className="absolute top-0 left-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border shadow-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200">
            <span className="text-base leading-none" aria-hidden="true">📞</span>
            <span>Check-In Call</span>
          </span>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {firstName ? `${greeting}, ${firstName}` : greeting}
            <span className="ml-2" aria-hidden="true">👋</span>
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{fmtDate(new Date())} — here's what's happening across your business.</p>
        </div>

        <div className="mb-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0 text-xl">📝</div>
              <div>
                <p className="font-semibold">Your {new Date().toLocaleDateString('en-US', { month: 'long' })} check-in is ready</p>
                <p className="text-sm text-white/80">Two minutes to tell us how things are going.</p>
              </div>
            </div>
            <span className="px-4 py-2 rounded-lg bg-white text-blue-700 text-sm font-semibold">Complete Check In</span>
          </div>
        </div>

        <div className="grid gap-3 grid-cols-2 xl:grid-cols-4 mb-6">
          {metrics.map(([emoji, count, label, tile, text]) => (
            <div key={label} className={`${CARD} p-4`}>
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tile}`}>
                  <span className="text-2xl" aria-hidden="true">{emoji}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-2xl font-bold leading-none ${text}`}>{count}</p>
                  <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400 truncate">{label}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0" />
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mb-6">
          <WhatsNewCard className="xl:col-span-8" />
          <ImplementationHoursCard className="xl:col-span-4" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mb-6">
          <BreakthroughHero className="xl:col-span-4" />
          <TasksCard className="xl:col-span-4" />
          <CommunityCard className="xl:col-span-4" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <RoadmapCard className="xl:col-span-8" />
          <GoalsCard className="xl:col-span-4" />
        </div>
      </div>
    </div>
  );
};

/* ---------------- Department hubs: shared shell + Sales / Operations / Technology pages ---------------- */

const HUB_TABS = {
  sales: [['📋', 'All'], ['📂', 'Materials'], ['📊', 'Analytics'], ['💲', 'Design Pricing'], ['🧮', 'Build Payments']],
  operations: [['📋', 'All'], ['📖', 'SOPs'], ['📈', 'KPIs'], ['💼', 'Job Descriptions'], ['📂', 'Materials'], ['🏢', 'Org Chart']],
  technology: [['📋', 'All'], ['🤖', 'Agents'], ['📥', 'JobTread Imports'], ['✨', 'Skills'], ['⚡', 'Automation'], ['🖥️', 'Tech Stack']],
  finance: [['📋', 'All'], ['📊', 'Analytics'], ['🧮', 'Margin Calculator'], ['💸', 'Overhead'], ['👷', 'Labor Burden'], ['📂', 'Materials']],
  marketing: [['📋', 'All'], ['📊', 'Analytics'], ['🌐', 'Website'], ['⭐', 'Reviews'], ['🧩', 'Website Plugins'], ['🧮', 'Lead Calculator']],
  hr: [['📋', 'All'], ['📂', 'Materials'], ['🤝', 'Hiring'], ['🧭', 'Mission Vision Values']],
};

// Mirrors PortalSales / PortalOperations / Technology: header buttons, centered title, tab nav, embedded tab body.
const HubScreen = ({ title, hub, activeTab, children }) => (
  <div className={`${PORTAL_BG} p-8`}>
    <div className="relative mb-6">
      <span className="absolute top-0 left-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border shadow-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200">
        <span className="text-base leading-none" aria-hidden="true">📞</span>
        <span>Schedule Call</span>
      </span>
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">{title}</h1>
      </div>
      <span className="absolute top-0 right-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border shadow-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200">
        <span className="text-base leading-none" aria-hidden="true">💬</span>
        <span>Ask Question</span>
      </span>
    </div>
    <nav className="flex flex-wrap justify-center gap-2 mb-6 px-2">
      {HUB_TABS[hub].map(([emoji, label]) => (
        <span
          key={label}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
            label === activeTab ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-800' : 'bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200'
          }`}
        >
          <span className="text-base leading-none shrink-0" aria-hidden="true">{emoji}</span>
          {label}
        </span>
      ))}
    </nav>
    <div className="mb-8 min-h-[200px]">{children}</div>
  </div>
);

const OperationsHubScreen = () => (
  <HubScreen title="Operations" hub="operations" activeTab="All">
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
      <CommunityCard />
      <RoadmapCard />
      <GoalsCard />
    </div>
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <WhatsNewCard items={OPS_WHATS_NEW} />
      <ImplementationHoursCard />
    </div>
  </HubScreen>
);

/* ---- shared "materials" list pieces (PillarMaterialsSection + SOPs share this layout) ---- */

const SearchRow = ({ placeholder, groups }) => (
  <div className="flex flex-col sm:flex-row gap-3 mb-6">
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
      <div className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm bg-white border-gray-200 text-gray-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400">{placeholder}</div>
    </div>
    <div className="sm:w-56 px-3 py-2 rounded-lg border text-sm bg-white border-gray-200 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-white">{groups}</div>
  </div>
);

const GroupHeader = ({ emoji, label }) => (
  <div className="flex items-center justify-center gap-3 mb-5">
    <span className="h-px flex-1 max-w-[110px] bg-gray-200 dark:bg-gray-700" />
    <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
      <span className="text-xl leading-none">{emoji}</span>
    </span>
    <h2 className="text-xl md:text-2xl font-bold tracking-tight leading-tight text-center text-gray-900 dark:text-white">{label}</h2>
    <span className="h-px flex-1 max-w-[110px] bg-gray-200 dark:bg-gray-700" />
  </div>
);

const ghostBtn = 'flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-200';
const tailorBtn = 'flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white';
const tailoredBtn = 'flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white';

const DocCard = ({ emoji, title, description, version, tailored }) => (
  <div className="rounded-xl border p-4 border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
    <div className="flex items-center gap-3 mb-4">
      <span className="shrink-0 w-11 h-11 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center">
        <span className="text-2xl leading-none">{emoji}</span>
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <h4 className="text-base md:text-lg font-bold leading-snug truncate text-gray-900 dark:text-white">{title}</h4>
          {version && (
            <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none bg-gray-100 text-gray-500 border border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700">v{version}</span>
          )}
        </div>
        {description && <p className="text-xs mt-0.5 text-gray-500 dark:text-gray-400">{description}</p>}
      </div>
    </div>
    <div className="flex items-center gap-2">
      <span className={ghostBtn}><Eye className="w-3.5 h-3.5" />View</span>
      <span className={tailored ? tailoredBtn : tailorBtn}><Sparkles className="w-3.5 h-3.5" />{tailored ? 'Tailored' : 'Tailor'}</span>
    </div>
  </div>
);

const DocGroups = ({ groups }) =>
  groups.map((g) => (
    <div key={g.label} className="mt-10 first:mt-0">
      <GroupHeader emoji={g.emoji} label={g.label} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {g.items.map((it) => <DocCard key={it.title} {...it} />)}
      </div>
    </div>
  ));

/* ---- Sales ▸ Materials (/portal/sales/resources) ---- */

// Same sections as templatesStructures/sales (CRM ▸ Templates ▸ Sales Materials).
const SALES_MATERIALS = [
  { emoji: '📝', label: 'Proposals & Contracts', items: [
    { emoji: '🎨', title: 'Design Proposal', description: 'Present your design phase and pricing', version: '1.0', tailored: true },
    { emoji: '🖋️', title: 'Design Contract', description: 'Formalize the design agreement', version: '1.0', tailored: true },
    { emoji: '🔨', title: 'Build Proposal', description: 'Lay out the full construction scope and cost', version: '1.0' },
    { emoji: '📝', title: 'Build Contract', description: 'Lock in build terms and payment schedule', version: '1.0' },
    { emoji: '💰', title: 'PD Pricing Sheet', description: 'Pricing guide for estimating', version: '1.0' },
    { emoji: '🛠️', title: 'Subcontractor Agreement', description: 'Establishes subcontracting policies', version: '1.0' },
    { emoji: '🗄️', title: 'Cabinet Only Contract', description: 'Cabinetry agreement', version: '1.0' },
  ] },
  { emoji: '🏠', label: 'Home Owners Resources', items: [
    { emoji: '❓', title: 'Intake Questionnaire', description: 'Gathers initial remodel data', version: '1.0' },
    { emoji: '🏠', title: 'Home Owners Remodeling Guide', description: 'Walk clients through the remodel journey', version: '1.0', tailored: true },
    { emoji: '✋', title: 'Welcome Home Neighbor', description: 'Introduce yourself to the neighborhood', version: '1.0' },
    { emoji: '📢', title: 'Partner Referral Program', description: 'Reward partners who send you leads', version: '1.0' },
    { emoji: '⬅️', title: 'Before Construction Begins', description: 'Homeowner guide explaining pre-construction planning', version: '1.0' },
  ] },
  { emoji: '🛠️', label: 'Project Resources', items: [
    { emoji: '🖥️', title: 'Project Website', description: 'Give clients a live project hub', version: '1.0' },
    { emoji: '💌', title: 'Thank You Cards with Review', description: 'Thank clients and prompt a review', version: '1.0', tailored: true },
    { emoji: '📑', title: 'Project Booklet Template', description: 'Package the project details in one piece', version: '1.0' },
    { emoji: '🏠', title: 'Open House Neighbors Mailer', description: 'Invite neighbors to the finished project', version: '1.0' },
    { emoji: '💨', title: 'Pardon Our Dust Mailer', description: 'Give neighbors a heads-up on the work', version: '1.0' },
  ] },
  { emoji: '💰', label: 'Sales Scripts', items: [
    { emoji: '🆕', title: 'Lead Intake', description: 'Qualify new leads consistently', version: '1.0', tailored: true },
    { emoji: '📞', title: 'Phone Consultation', description: 'Guide the first call with confidence', version: '1.0' },
    { emoji: '🏠', title: 'In Home Visit', description: 'Run a structured on-site meeting', version: '1.0' },
    { emoji: '📝', title: 'Proposal Meeting', description: 'Close with a clear proposal walkthrough', version: '1.0' },
  ] },
  { emoji: '🔑', label: 'Realtors', items: [
    { emoji: '🤝', title: 'Realtor Partner Guide', description: 'Create realtor partners', version: '1.0' },
    { emoji: '📋', title: 'Realtor Scripts', description: 'Conversation guides', version: '1.0' },
  ] },
];
const SalesMaterialsScreen = () => (
  <HubScreen title="Sales" hub="sales" activeTab="Materials">
    <SearchRow placeholder="Search materials..." groups="All groups" />
    <DocGroups groups={SALES_MATERIALS} />
  </HubScreen>
);

/* ---- Operations ▸ SOPs (/portal/operations/sops) ---- */

// The real sopTemplates library, grouped the way Operations ▸ SOPs groups it.
const SOP_GROUPS = [
  { emoji: '📐', label: 'Design', items: [
    { emoji: '🌐', title: 'Design Process Overview', description: 'Guide a project from first call to signed build contract.', version: '1.1', tailored: true },
    { emoji: '🔑', title: 'Design Proposal & Presentation SOP', description: 'Present and close a design proposal in person.', version: '1.1' },
    { emoji: '📢', title: 'Project Briefing SOP', description: 'Align the design team on scope before the measure meeting', version: '1.1' },
    { emoji: '📐', title: 'Measure Meeting SOP', description: 'Capture accurate measurements and kick off client selections', version: '1.1', tailored: true },
    { emoji: '🏠', title: 'Existing Conditions SOP', description: 'Produce accurate existing-condition drawings', version: '1.1' },
    { emoji: '🔒', title: 'Redline SOP', description: 'Present proposed designs and lock in a clear direction', version: '1.1' },
    { emoji: '📃', title: 'Estimate SOP', description: 'Give clients an early, realistic price to guide design decisions', version: '1.1' },
    { emoji: '🛁', title: 'Selections SOP', description: 'Guide clients through finish selections', version: '1.1' },
    { emoji: '💭', title: 'Inspiration Board SOP', description: 'Organize client ideas into a project vision board.', version: '1.1' },
    { emoji: '🔍', title: 'Estimator SOP', description: 'Consistent, accurate estimating from measure to contract.', version: '1.1' },
    { emoji: '👷‍♂️', title: 'Trades Day SOP', description: 'Host subcontractors on site to gather accurate bids', version: '1.1' },
    { emoji: '✏️', title: 'Construction Drawings SOP', description: 'Produce permit-ready construction drawings', version: '1.1' },
    { emoji: '📃', title: 'Semi Final Drawings SOP', description: 'Produce a pricing set that defines scope for estimation.', version: '1.1' },
    { emoji: '🪲', title: 'Debugging SOP', description: 'Systematically catch proposal errors before they reach contract', version: '1.1' },
    { emoji: '🧾', title: 'Debugging Checklist Template SOP', description: 'Vet every cost code before contract to eliminate costly misses', version: '1.1' },
    { emoji: '🔍', title: 'Quality Control SOP', description: 'Catch estimate errors before the proposal goes out', version: '1.1' },
    { emoji: '💰', title: 'Budget Meeting SOP', description: 'Facilitate a finalized proposal and secure client approval.', version: '1.1' },
    { emoji: '📝', title: 'Build Proposal & Contract SOP', description: 'Convert an approved estimate into a signed build contract', version: '1.1' },
    { emoji: '🤝', title: 'Preconstruction Design Handoff SOP', description: 'Hand off a complete project from design to build', version: '1.1' },
    { emoji: '🛠️', title: 'Build Involvement SOP', description: 'Keep design support active through the build phase', version: '1.1' },
    { emoji: '🎨', title: 'Design Sub-Contractor SOP', description: 'Set clear terms and expectations for 1099 design subcontractors', version: '1.1' },
  ] },
  { emoji: '👷', label: 'Construction', items: [
    { emoji: '🔦', title: 'Pre-Construction Walkthrough SOP', description: 'Transition a job from design to build and set client expectations', version: '1.1', tailored: true },
    { emoji: '👷‍♂️', title: 'PM SOP', description: 'Run a project from design handoff through build completion and review', version: '1.1' },
    { emoji: '📸', title: 'Photo & Job Logs SOP', description: 'Document daily progress and photos as a build record', version: '1.1', tailored: true },
    { emoji: '🔁', title: 'Change Order SOP', description: 'Price, approve, and process change orders before work begins', version: '1.1' },
    { emoji: '⛈️', title: 'Imminent Weather SOP', description: 'Secure the jobsite and materials ahead of severe weather', version: '1.1' },
    { emoji: '🚩', title: 'Red Flag Email SOP', description: 'Document and resolve costly build issues before they compound', version: '1.1' },
    { emoji: '🔒', title: 'Project Close Out SOP', description: 'Systematically close a project and hand the home back to the client', version: '1.1' },
    { emoji: '🔍', title: 'Post Mortem SOP', description: 'Analyze a finished project to capture wins and improve the process', version: '1.1' },
  ] },
  { emoji: '🏢', label: 'Office', items: [
    { emoji: '📞', title: 'Client Intake SOP', description: 'Capture, qualify, and route new leads to the Project Director', version: '1.1', tailored: true },
    { emoji: '🔑', title: 'Project Director SOP', description: 'The full PD playbook from lead through build and closeout', version: '1.1' },
    { emoji: '🧾', title: 'PD Training SOP', description: 'Train a new PD to run projects from lead to closeout', version: '1.1' },
    { emoji: '💎', title: 'Exec SOP', description: 'Guide the COO in running daily operations, teams, and performance', version: '1.1' },
    { emoji: '🆕', title: 'Hiring & Onboarding SOP', description: 'Recruit, evaluate, hire, and onboard new employees consistently', version: '1.1' },
    { emoji: '❌', title: 'Termination & Disciplinary SOP', description: 'Handle resignations, discipline, and terminations', version: '1.1' },
    { emoji: '🤝', title: 'Sub Contractor Handoff SOP', description: 'Align each trade partner on scope, price, and timing before work starts', version: '1.1' },
    { emoji: '🚛', title: 'Shipping & Receiving SOP', description: 'Control every delivery from receipt to jobsite storage', version: '1.1' },
    { emoji: '💰', title: 'Build Payment Calculator SOP', description: 'Generate clear, staggered payment milestones from a contract price', version: '1.1' },
    { emoji: '👷‍♂️', title: 'Trade Partner SOP', description: 'Recruit, vet, and onboard trade partners into a win-win partnership', version: '1.1' },
    { emoji: '💰', title: 'Compensation SOP', description: 'Building and issuing compensation plans', version: '1.0' },
  ] },
];

// Static take on src/components/sop/ContactToBuildFlow.js: serpentine step cards + orange arrows.
const CTB = { W: 240, H: 128, GX: 64, GY: 88, PAD: 48, COLS: 5, ARROW: '#F5843C', SCALE: 0.62 };
const ContactToBuildBoard = () => {
  const steps = CONTACT_TO_BUILD_STEPS;
  const rows = Math.max(1, Math.ceil(steps.length / CTB.COLS));
  const canvasW = CTB.PAD * 2 + CTB.COLS * CTB.W + (CTB.COLS - 1) * CTB.GX;
  const canvasH = CTB.PAD * 2 + rows * CTB.H + (rows - 1) * CTB.GY;
  const pos = steps.map((_, i) => {
    const row = Math.floor(i / CTB.COLS);
    const col = i % CTB.COLS;
    const visualCol = row % 2 === 0 ? col : CTB.COLS - 1 - col;
    return { x: CTB.PAD + visualCol * (CTB.W + CTB.GX), y: CTB.PAD + row * (CTB.H + CTB.GY), row };
  });
  const arrows = pos.slice(0, -1).map((from, i) => {
    const to = pos[i + 1];
    if (from.row === to.row) {
      const ltr = to.x > from.x;
      const y = from.y + CTB.H / 2;
      return <line key={i} x1={ltr ? from.x + CTB.W : from.x} y1={y} x2={ltr ? to.x - 7 : to.x + CTB.W + 7} y2={y} stroke={CTB.ARROW} strokeWidth="2" markerEnd="url(#pf-ctb-arrow)" />;
    }
    const x = from.x + CTB.W / 2;
    return <line key={i} x1={x} y1={from.y + CTB.H} x2={x} y2={to.y - 7} stroke={CTB.ARROW} strokeWidth="2" markerEnd="url(#pf-ctb-arrow)" />;
  });
  return (
    <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900">
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1 rounded-lg border px-1 py-0.5 shadow-sm bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
        <span className="px-2 py-1 rounded-md text-xs font-semibold text-gray-600 dark:text-gray-300">Fit</span>
        <span className="w-px h-4 bg-gray-200 dark:bg-gray-700" />
        <span className="p-1.5 text-gray-600 dark:text-gray-300"><ZoomOut className="w-4 h-4" /></span>
        <span className="text-[11px] w-10 text-center tabular-nums text-gray-500 dark:text-gray-400">{Math.round(CTB.SCALE * 100)}%</span>
        <span className="p-1.5 text-gray-600 dark:text-gray-300"><ZoomIn className="w-4 h-4" /></span>
        <span className="w-px h-4 bg-gray-200 dark:bg-gray-700" />
        <span className="p-1.5 text-gray-600 dark:text-gray-300"><Maximize2 className="w-4 h-4" /></span>
      </div>
      <div className="h-[520px] select-none">
        <div className="relative" style={{ width: canvasW, height: canvasH, transform: `translate(12px, 12px) scale(${CTB.SCALE})`, transformOrigin: '0 0' }}>
          <svg width={canvasW} height={canvasH} className="absolute inset-0 pointer-events-none">
            <defs>
              <marker id="pf-ctb-arrow" markerWidth="9" markerHeight="9" refX="7.4" refY="3.2" orient="auto">
                <path d="M0,0 L7.4,3.2 L0,6.4 Z" fill={CTB.ARROW} />
              </marker>
            </defs>
            {arrows}
          </svg>
          {steps.map((step, i) => (
            <div key={i} style={{ left: pos[i].x, top: pos[i].y, width: CTB.W, height: CTB.H }} className="absolute rounded-xl border p-3 overflow-hidden bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
              <div className="flex items-start gap-2">
                <span className="shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900">{i + 1}</span>
                <span className="text-xs font-bold leading-snug text-gray-900 dark:text-white">{step.action}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {String(step.assignedTo || '').split('/').map((r) => r.trim()).filter(Boolean).map((role, r) => (
                  <span key={r} className="px-1.5 py-0.5 rounded-full border text-[9px] font-semibold border-red-100 bg-red-50 text-red-600 dark:border-red-900/60 dark:bg-red-900/30 dark:text-red-300">{role}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[11px] px-2 py-0.5 rounded-full pointer-events-none bg-white/90 text-gray-500 dark:bg-gray-800/90 dark:text-gray-400">
        Scroll or drag to pan · click a step to open the SOP
      </div>
    </div>
  );
};

const SopsScreen = () => (
  <HubScreen title="Operations" hub="operations" activeTab="SOPs">
    <SearchRow placeholder="Search SOPs..." groups="All groups" />
    <DocGroups groups={SOP_GROUPS} />
    <div className="mt-10">
      <GroupHeader emoji="📋" label="Contact to Build" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <DocCard emoji="📋" title="Contact to Build SOP" description={CONTACT_TO_BUILD_GOAL} tailored />
      </div>
      <div className="mt-6">
        <ContactToBuildBoard />
      </div>
    </div>
  </HubScreen>
);
/* ---- Operations ▸ Org Chart (/portal/operations/org-chart) ---- */

const ORG = [
  { id: 'o1', name: 'Mike Carter', title: 'Owner', header: 'bg-red-500', grade: 'A', tenure: '12 yrs', salary: '$140,000', reportsTo: null },
  { id: 'o2', name: 'Dana Ruiz', title: 'Director of Operations', header: 'bg-blue-500', grade: 'A', tenure: '5 yrs', salary: '$95,000', reportsTo: 'o1' },
  { id: 'o3', name: 'Chris Lee', title: 'Project Manager', header: 'bg-blue-500', grade: 'B', tenure: '3 yrs', salary: '$78,000', reportsTo: 'o1' },
  { id: 'o4', name: 'Priya Shah', title: 'Designer', header: 'bg-purple-500', grade: 'A', tenure: '2 yrs', salary: '$68,000', reportsTo: 'o1' },
  { id: 'o5', name: 'Tom Alvarez', title: 'Bookkeeper', header: 'bg-green-500', grade: 'B', tenure: '4 yrs', salary: '$58,000', reportsTo: 'o1' },
  { id: 'o6', name: 'Sam Oduya', title: 'Lead Carpenter', header: 'bg-blue-500', grade: 'B', tenure: '6 yrs', salary: '$72,000', reportsTo: 'o2' },
  { id: 'o7', name: 'Open Position', title: 'Estimator', header: 'bg-blue-500', grade: '', tenure: 'Hire Q2', salary: '$65,000', reportsTo: 'o2', future: 1 },
];

const GRADE_COLOR = { A: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200', B: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200' };

const EmployeeCard = ({ emp }) => (
  <div className="employee-card rounded-lg shadow-md overflow-hidden border border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800 w-[200px]">
    <div className={`${emp.header} w-full py-2 px-3 text-center text-white`}>
      <h3 className="font-medium">{emp.name}</h3>
      <p className="text-xs">{emp.title}</p>
    </div>
    <div className="p-3 flex items-center">
      <div className="mr-3">
        <div className={`rounded-full flex items-center justify-center w-12 h-12 ${emp.future ? 'bg-blue-500 text-white' : 'bg-green-600 text-white'}`}>
          {emp.future ? <span className="font-bold text-lg">{emp.future}</span> : <Check size={26} strokeWidth={3} />}
        </div>
      </div>
      <div className="flex-1 space-y-1.5">
        {emp.grade && <div className={`px-2 py-0.5 rounded text-xs w-full text-center ${GRADE_COLOR[emp.grade]}`}>Grade: {emp.grade}</div>}
        <div className="px-2 py-0.5 rounded text-xs w-full text-center bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">{emp.tenure}</div>
        <div className="px-2 py-0.5 rounded text-xs w-full text-center bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">{emp.salary}</div>
      </div>
    </div>
  </div>
);

const VLine = ({ h = 20 }) => <div style={{ width: 4, height: h, backgroundColor: '#9CA3AF', margin: '0 auto' }} />;

const Reports = ({ parentId }) => {
  const kids = ORG.filter((e) => e.reportsTo === parentId);
  if (!kids.length) return null;
  return (
    <div className="flex flex-col items-center">
      <VLine />
      <div className="relative">
        {kids.length > 1 && <div className="absolute top-0 left-[100px] right-[100px] h-1 bg-gray-400" />}
        <div className="flex justify-center gap-6">
          {kids.map((k) => (
            <div key={k.id} className="flex flex-col items-center">
              <VLine />
              <EmployeeCard emp={k} />
              <Reports parentId={k.id} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const OrgChartScreen = () => {
  const totalSalary = ORG.reduce((s, e) => s + Number(e.salary.replace(/[^0-9.-]+/g, '')), 0);
  return (
    <HubScreen title="Operations" hub="operations" activeTab="Org Chart">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div className="flex items-center gap-8 text-xl text-black dark:text-white">
          <div className="flex items-center gap-3">
            <span className="font-semibold">Headcount:</span>
            <span>{ORG.length}</span>
            <span className="p-1 rounded-full bg-green-600 text-white"><Plus size={18} strokeWidth={2.5} /></span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-semibold">Total Salary:</span>
            <span>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(totalSalary)}</span>
          </div>
        </div>
        <span className="px-3 py-1 rounded flex items-center gap-2 bg-red-500 text-white text-sm"><Users className="w-4 h-4" />1099&apos;s</span>
        <div className="flex items-center gap-4">
          <span className="px-3 py-1 rounded flex items-center gap-2 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 text-sm">Recenter</span>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold"><Plus size={16} />Add Member</span>
        </div>
      </div>
      <div className="w-full flex justify-center overflow-x-auto pb-6">
        <div className="flex flex-col items-center pt-4 min-w-max">
          <EmployeeCard emp={ORG[0]} />
          <Reports parentId="o1" />
        </div>
      </div>
    </HubScreen>
  );
};

/* ---- Technology ▸ Tech Stack (/portal/technology/tech-stack) ---- */

const TECH_STACK = [
  { title: 'Core', color: 'bg-red-700', width: '40%', tools: [
    ['Project Management', 'JobTread', '🏗️', true],
    ['Accounting', 'QuickBooks', '📒', true],
    ['Estimating', 'JobTread', '🧮', true],
  ] },
  { title: 'Primary', color: 'bg-red-600', width: '70%', tools: [
    ['Email & Docs', 'Google Workspace', '📧', true],
    ['Design', 'Chief Architect', '📐', false],
    ['Payments', 'Stripe', '💳', true],
    ['Phone', 'RingCentral', '📱', true],
    ['Scheduling', 'JobTread', '📅', true],
  ] },
  { title: 'Secondary', color: 'bg-red-500', width: '100%', tools: [
    ['Website', 'WordPress', '🌐', true],
    ['Reviews', 'NiceJob', '⭐', true],
    ['Social', 'Meta Business', '📣', false],
    ['Automation', 'Zapier', '⚡', true],
    ['AI Assistant', 'Claude', '🤖', true],
    ['E-Signature', 'DocuSign', '✍️', true],
    ['Selections', 'None', '✗', false],
  ] },
];

const TechStackScreen = () => (
  <HubScreen title="Technology" hub="technology" activeTab="Tech Stack">
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col items-center mb-4 space-y-3">
        <div className="flex space-x-3">
          <span className="bg-green-600 text-white px-4 py-2 rounded-md text-sm flex items-center space-x-2"><span>💡</span><span>Proposed List (2)</span></span>
        </div>
        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-300">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full border-2 border-green-500 bg-gray-100" />
            <span>Open API Available</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-center space-y-4 mb-12">
        {TECH_STACK.map((cat) => (
          <div key={cat.title} className="w-full flex justify-center">
            <div className={`${cat.color} rounded-lg shadow-lg p-3`} style={{ width: cat.width }}>
              <div className="flex justify-center items-center mb-3">
                <h2 className="text-xl font-bold text-white text-center">{cat.title}</h2>
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                {cat.tools.map(([name, software, emoji, open]) => {
                  const none = software === 'None';
                  return (
                    <div key={name} className="flex flex-col items-center w-20">
                      <div className="relative">
                        <div className={`w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center shadow-sm border-2 overflow-hidden ${open && !none ? 'border-green-500' : 'border-transparent'}`}>
                          {none ? <span className="text-red-600 dark:text-red-400 text-xl font-medium">✗</span> : <span className="text-lg leading-none">{emoji}</span>}
                        </div>
                        {open && !none && <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm" />}
                      </div>
                      <h3 className="font-medium text-center text-xs text-white mt-1">{name}</h3>
                      {!none && <span className="text-xs text-red-100 mt-0.5 text-center truncate max-w-full">{software}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </HubScreen>
);

/* ---------------- Analytics widgets (mirror src/components/finance/MetricWidget.js) ---------------- */

const STATUS = {
  good: { text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', dot: 'bg-emerald-500', label: 'On track' },
  ok: { text: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', dot: 'bg-amber-500', label: 'Watch' },
  poor: { text: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20', dot: 'bg-red-500', label: 'Needs attention' },
};
const SOURCE = {
  jobtread: { label: 'JobTread', classes: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  manual: { label: 'Your inputs', classes: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
};

// Mirrors src/components/finance/MetricWidget.js: header (icon · title · meta · source badge),
// headline value with benchmark status pill, sub line, chart slot, footer note.
const Widget = ({ title, titleMeta, icon: Icon, value, sub, status = null, source = null, hidePill = false, headerAction, footer, compact = false, stretch = false, children, className = '' }) => {
  const st = status ? STATUS[status] : null;
  const badge = source ? SOURCE[source] : null;
  return (
    <div className={`flex flex-col rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm ${compact ? 'p-4' : 'p-5'} ${stretch ? 'h-full' : ''} ${className}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <span className="text-red-600 text-lg shrink-0"><Icon className="w-4 h-4" /></span>}
          <h3 className="font-semibold text-gray-800 dark:text-white truncate">{title}</h3>
          {titleMeta && <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{titleMeta}</span>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {headerAction}
          {badge && <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${badge.classes}`}>{badge.label}</span>}
        </div>
      </div>
      {value != null && (
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className={`${compact ? 'text-2xl' : 'text-3xl'} font-bold ${st ? st.text : 'text-gray-900 dark:text-white'}`}>{value}</span>
          {st && !hidePill && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${st.bg} ${st.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
              {st.label}
            </span>
          )}
        </div>
      )}
      {sub && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</div>}
      {children && <div className={`mt-3 ${stretch ? 'flex-1 min-h-0 flex flex-col' : ''}`}>{children}</div>}
      {footer && <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-700 text-center text-xs text-gray-500 dark:text-gray-400">{footer}</div>}
    </div>
  );
};
const JtPill = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
    <CheckCircle2 className="w-2.5 h-2.5" /> JobTread
  </span>
);

const AnalyticsHeader = ({ text, gear = false }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
    <p className="text-sm text-gray-500 dark:text-gray-400">
      {text} {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
    </p>
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
        <CheckCircle2 className="w-3 h-3" /> JobTread
      </span>
      <span className="px-3 py-1.5 rounded-lg border text-sm font-medium bg-white border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200">Last 12 months ▾</span>
      {gear && (
        <span className="p-2 rounded-lg border bg-white border-gray-200 text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"><Settings className="w-4 h-4" /></span>
      )}
    </div>
  </div>
);

const MONTHS = ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];
const GPM_SERIES = [28, 30, 27, 31, 33, 32, 35, 34, 36, 38, 37, 39].map((v, i) => ({ label: MONTHS[i], value: v }));
const LEADS_SERIES = [14, 12, 9, 16, 19, 22, 25, 24, 27, 30, 28, 31].map((v, i) => ({ label: MONTHS[i], value: v }));

const MiniArea = ({ data, color, id, pct = false }) => (
  <div className="flex-1 min-h-[16rem]">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.25} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={(v) => (pct ? `${v}%` : v)} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={44} />
        <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={`url(#${id})`} />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

/* ---- Finance ▸ Analytics (/portal/finance/analytics) ---- */

const FinanceAnalyticsScreen = () => (
  <HubScreen title="Finance" hub="finance" activeTab="Analytics">
    <AnalyticsHeader text="Your live profitability picture, as of" />
    <div className="grid grid-cols-1 xl:grid-cols-4 gap-5 mb-6">
      <div className="xl:col-span-3 flex flex-col [&>div]:flex-1">
        <Widget stretch title="Gross Profit Margin" icon={Percent} value="39.2%" status="good" hidePill source="jobtread">
          <MiniArea data={GPM_SERIES} color="#10b981" id="pfGpm" pct />
          <div className="flex items-center gap-2 mt-2 text-sm font-medium text-gray-600 dark:text-gray-300">
            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: '#10b981' }} />
            Gross profit margin
          </div>
        </Widget>
      </div>
      <div className="flex flex-col gap-5 [&>div]:flex-1">
        <Widget title="Gross Profit" icon={DollarSign} value="$412,000" status="good" source="jobtread" sub="on $1,050,000 of signed work" />
        <Widget title="Net Margin" icon={TrendingUp} value="12.4%" status="good" source="jobtread" sub="after $264,000 overhead" />
        <Widget title="Net Profit" icon={Wallet} value="$148,000" status="good" source="jobtread" sub="gross profit minus overhead" />
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Widget title="Receivables & Payables" icon={ArrowLeftRight} source="jobtread">
        <div className="space-y-4">
          {[
            ['Receivable', '$86,400', 'bg-emerald-500', 'text-emerald-600 dark:text-emerald-400', ArrowDownToLine, 100],
            ['Payable', '$41,200', 'bg-rose-500', 'text-rose-600 dark:text-rose-400', ArrowUpFromLine, 48],
          ].map(([label, val, bar, iconCls, Icon, w]) => (
            <div key={label}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300"><Icon className={`w-3.5 h-3.5 ${iconCls}`} />{label}</span>
                <span className="text-base font-bold text-gray-900 dark:text-white">{val}</span>
              </div>
              <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden"><div className={`h-full rounded-full ${bar}`} style={{ width: `${w}%` }} /></div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1 border-t border-gray-100 dark:border-gray-700">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Net position</span>
            <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">$45,200</span>
          </div>
        </div>
      </Widget>
      <Widget title="Overhead" titleMeta="Quick total" icon={Wallet} value="$22,000" source="manual" sub="per month · $264,000 per year">
        <div className="flex items-center gap-2">
          <div className="flex-1 px-3 py-1.5 rounded-lg border text-sm bg-white border-gray-200 text-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400">$ 22,000</div>
          <span className="px-3 py-1.5 rounded-lg border text-xs font-medium bg-white border-gray-200 text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">Monthly ▾</span>
          <span className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium">Save</span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">Saves as one fixed total and updates your Overhead Calculator.</p>
      </Widget>
    </div>
  </HubScreen>
);

/* ---- Marketing ▸ Analytics (/portal/marketing/analytics) ---- */

const LEAD_SOURCES = [['Referral', 118], ['Google Search', 64], ['Houzz', 31], ['Instagram', 22], ['Yard Signs', 14]];
const RANK_BADGES = ['bg-amber-400 text-white', 'bg-gray-400 text-white', 'bg-orange-400 text-white', 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300', 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'];
const RANK_FILLS = ['#dc2626', '#f97316', '#f59e0b', '#9ca3af', '#d1d5db'];

const MarketingAnalyticsScreen = () => {
  const attributed = LEAD_SOURCES.reduce((s, [, n]) => s + n, 0);
  return (
    <HubScreen title="Marketing" hub="marketing" activeTab="Analytics">
      <AnalyticsHeader text="Your live marketing picture, as of" gear />
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-5 mb-6">
        <div className="xl:col-span-3 flex flex-col [&>div]:flex-1">
          <Widget stretch title="Leads" icon={Users} value="257" headerAction={<JtPill />}>
            <MiniArea data={LEADS_SERIES} color="#dc2626" id="pfLeads" />
            <div className="flex items-center gap-2 mt-2 text-sm font-medium text-gray-600 dark:text-gray-300"><span className="w-3 h-3 rounded-full bg-red-600 shrink-0" />Leads</div>
          </Widget>
        </div>
        <div className="flex flex-col gap-5 [&>div]:flex-1">
          <Widget stretch title="Top Lead Sources" icon={Award} headerAction={<JtPill />} footer={`${attributed} of 257 leads attributed`}>
            <div className="h-full flex flex-col gap-2">
              {LEAD_SOURCES.map(([name, count], i) => (
                <div key={name} className="flex-1 flex flex-col justify-center min-h-0">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className={`w-[18px] h-[18px] text-[10px] shrink-0 rounded-full flex items-center justify-center font-bold ${RANK_BADGES[i]}`}>{i + 1}</span>
                      <span className="truncate font-medium text-gray-700 dark:text-gray-200">{name}</span>
                    </span>
                    <span className="font-semibold text-gray-500 dark:text-gray-400 shrink-0 ml-2">{count} · {Math.round((count / attributed) * 100)}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(count / LEAD_SOURCES[0][1]) * 100}%`, backgroundColor: RANK_FILLS[i] }} /></div>
                </div>
              ))}
            </div>
          </Widget>
        </div>
      </div>
      <div className="rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-6">
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-4">
          <span className="text-red-600"><Target className="w-4 h-4" /></span>
          <h3 className="font-semibold text-gray-800 dark:text-white">Leads Needed</h3>
          <span className="text-xs text-gray-500 dark:text-gray-400">to hit your <span className="font-semibold text-gray-700 dark:text-gray-200">$1,500,000</span> revenue goal</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[['Revenue goal', '$1.5M'], ['Avg job size', '$85,000'], ['Jobs needed', '18'], ['Proposals needed', '47', 'at 38% close ratio'], ['Leads needed', '79', 'at 60% qualified']].map(([l, v, s]) => (
            <div key={l} className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3 text-center">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{l}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white mt-0.5">{v}</p>
              {s && <p className="text-[10px] text-gray-500 dark:text-gray-400">{s}</p>}
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {[
          ['Social Media', Share2, '2,340', 'Total followers'],
          ['Paid Media', Megaphone, '$1,200', 'Total ad spend'],
          ['Newsletter', Mail, '860', 'Total subscribers'],
        ].map(([t, I, v, s]) => (
          <Widget key={t} compact title={t} icon={I} value={v} sub={s} headerAction={<span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300">Click to update</span>} />
        ))}
        <Widget compact title="Qualified Lead %" icon={Percent} value="60%" sub="154 of 257 answered qualified" headerAction={<JtPill />} />
      </div>
    </HubScreen>
  );
};

/* ---- HR ▸ Materials (/portal/hr/materials) ---- */

// Same groups and titles as the HR ▸ Materials defaults (src/utils/hrMaterials.js).
const HR_EMOJI = {
  hiring: '📋', onboarding: '✅', handbook: '📖',
  'Background Check': '🛡️', 'Interview Evaluation': '📝', 'Offer Letter': '✉️', 'Employment Application': '🗂️', 'Exit Interview Form': '🚪',
  'New Hire Checklist': '☑️', 'New Hire Onboarding': '👋', 'Direct Deposit': '🏦', 'New Hire Presentation': '📽️', 'Employee Referral Program': '🎁',
  'Drug Test Consent': '🧪', 'Non Compete': '📄', 'Employee Handbook': '📕',
};
const HR_TAILORED = new Set(['Offer Letter', 'New Hire Checklist', 'Employee Handbook', 'Interview Evaluation']);
const HR_MATERIALS = DEFAULT_HR_SECTIONS.map((sec) => ({
  emoji: HR_EMOJI[sec.id] || '📂',
  label: sec.label,
  items: sec.items.map((it) => ({
    emoji: HR_EMOJI[it.title] || '📄',
    title: it.name || it.title,
    description: it.description,
    version: it.title === 'Employee Handbook' ? '1.2' : '1.1',
    tailored: HR_TAILORED.has(it.title),
  })),
}));
const HRMaterialsScreen = () => (
  <HubScreen title="HR" hub="hr" activeTab="Materials">
    <SearchRow placeholder="Search materials..." groups="All groups" />
    <DocGroups groups={HR_MATERIALS} />
  </HubScreen>
);

/* ---- Courses (/portal/courses) ---- */

const COURSE_COLORS = { Sales: 'bg-red-600', Operations: 'bg-blue-600', Technology: 'bg-purple-600', Finance: 'bg-green-600', Marketing: 'bg-orange-500', HR: 'bg-yellow-500', General: 'bg-gray-500' };
// Fallback when the live course list can't load (titles match the real library).
const FALLBACK_COURSES = [
  { title: 'Scaling Roadmap', description: 'Build the structure needed to grow sustainably', category: 'General' },
  { title: 'Design SOPs', description: 'Proven systems for the design process', category: 'Operations' },
  { title: 'Markup vs Margin', description: 'Price jobs accurately and protect profit', category: 'Finance' },
  { title: 'Realtor Relationships', description: 'Turn local realtors into lead generation', category: 'Sales' },
  { title: 'Hiring Process', description: 'Follow a structured, repeatable hiring system', category: 'HR' },
  { title: 'Leads Calculator', description: 'Know exactly how many leads you need', category: 'Marketing' },
  { title: 'AI Receptionist', description: 'Handle calls, scheduling, and intake with AI', category: 'Technology' },
  { title: 'Job Descriptions', description: 'Clarify every role and responsibility', category: 'Operations' },
  { title: 'Financial Health Metrics', description: 'Track the metrics that drive profitability', category: 'Finance' },
];
const COURSE_PROGRESS = [100, 65, 40, 20, 0, 0, 100, 35, 0, 0, 0, 0];

// Reads the live course library (title, thumbnail, category) so the preview shows the real cards.
const useLiveCourses = () => {
  const [courses, setCourses] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, 'courses'))
      .then((snap) => {
        if (cancelled) return;
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => c.title)
          .sort((a, b) => String(a.title).localeCompare(String(b.title)));
        setCourses(rows.length ? rows : FALLBACK_COURSES);
      })
      .catch(() => { if (!cancelled) setCourses(FALLBACK_COURSES); });
    return () => { cancelled = true; };
  }, []);
  return courses;
};

const CoursesScreen = () => {
  const live = useLiveCourses();
  const courses = (live || FALLBACK_COURSES).slice(0, 12);
  const total = live ? live.length : 24;
  return (
    <div className={`${PORTAL_BG} p-8 text-gray-900 dark:text-white`}>
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Courses</h1>
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
        {[['Total', String(total), '🎓'], ['In Progress', '3', '▶️'], ['Completed', '2', '✅'], ['New Courses & Lessons', '4', '✨']].map(([l, v, e]) => (
          <div key={l} className="rounded-lg border p-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">{l}</span>
              <span className="text-base leading-none">{e}</span>
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">{v}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-6 !py-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400" />
            <div className="w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm bg-white border-gray-300 text-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400">Search courses...</div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
            <span className="px-3 py-2.5 rounded-lg border text-sm min-w-[140px] bg-white border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white">All Departments ▾</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {courses.map((c, i) => {
          const progress = COURSE_PROGRESS[i] ?? 0;
          const isNew = i === 1 || i === 4;
          return (
            <div key={c.id || c.title} className="rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm p-5 flex flex-col h-full relative">
              <div className={`absolute top-4 left-4 z-10 px-2.5 py-0.5 rounded-full text-[10px] font-medium text-white ${COURSE_COLORS[c.category] || COURSE_COLORS.General}`}>{c.category || 'General'}</div>
              {isNew && (
                <div className="absolute top-4 right-4 z-10 px-2.5 py-0.5 rounded-full text-[10px] font-medium text-white bg-red-600 flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" />New</div>
              )}
              {c.thumbnailUrl ? (
                <div className="mb-4 mt-6"><img src={c.thumbnailUrl} alt={c.title} className="w-full h-40 object-cover rounded-lg" /></div>
              ) : (
                <div className="mb-4 mt-6 flex justify-center">
                  <div className="w-full h-32 rounded-lg bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center"><Book className="w-10 h-10 text-red-600 opacity-60" /></div>
                </div>
              )}
              <div className="mb-3">
                <h3 className="font-semibold text-lg mb-1 line-clamp-2 text-gray-800 dark:text-white">{c.title}</h3>
                <p className="text-sm line-clamp-2 text-gray-500 dark:text-gray-400">{c.description}</p>
              </div>
              <div className="mt-auto">
                <div className="w-full rounded-full h-1.5 overflow-hidden mb-1.5 bg-gray-200 dark:bg-gray-700">
                  <div className={`${progress === 100 ? 'bg-emerald-500' : progress > 0 ? 'bg-red-600' : 'bg-gray-300'} h-1.5 rounded-full`} style={{ width: `${progress}%` }} />
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">{progress}% complete</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
/* ---------------- preview ---------------- */

const ProcessFlowPreview = ({ firstName = '', stepIndex = null, onStepChange = null }) => {
  const steps = useMemo(
    () => [
      {
        n: 1,
        title: 'Kickoff',
        screens: [
          {
            id: 'kickoff',
            label: 'Top Priorities',
            url: 'app.buildersbreakthrough.com/portal/kickoff',
            caption: 'On the kickoff call the client taps each card to rank it Low, Medium, or gold Top Priority, then drags their top picks into order. This is the map your BBA team works from.',
            sidebar: 'portal',
            render: () => <KickoffScreen />,
          },
        ],
      },
      {
        n: 2,
        title: 'Roadmap',
        screens: [
          {
            id: 'overview',
            label: 'Overview',
            url: 'app.buildersbreakthrough.com/portal/roadmap',
            caption: 'Overview shows every milestone across departments at a glance: complete, in progress, and what is coming next.',
            sidebar: 'portal',
            sidebarActive: 'roadmap',
            render: () => <RoadmapOverviewScreen />,
          },
          {
            id: 'focus',
            label: 'Current Focus',
            url: 'app.buildersbreakthrough.com/portal/roadmap',
            caption: 'The roadmap always opens on the current step. Each step is a short stepper of tasks with courses, portal pages, and call booking built in, plus quick ways to ask a question or book a call.',
            sidebar: 'portal',
            sidebarActive: 'roadmap',
            render: () => <RoadmapFocusScreen />,
          },
        ],
      },
      {
        n: 3,
        title: 'Scale',
        screens: [
          {
            id: 'sops',
            label: 'SOPs',
            url: 'app.buildersbreakthrough.com/portal/operations/sops',
            caption: 'The SOP library is grouped by Design, Construction, and Office. Each procedure opens as a full document the client tailors to how their company already works, plus the Contact to Build flow.',
            sidebar: 'portal',
            sidebarActive: 'operations',
            render: () => <SopsScreen />,
          },
          {
            id: 'operations',
            label: 'Operations hub',
            url: 'app.buildersbreakthrough.com/portal/operations/all',
            caption: 'Every department has its own hub with SOPs, KPIs, job descriptions, materials, and an org chart, each scoped to that department with its own roadmap and goals.',
            sidebar: 'portal',
            sidebarActive: 'operations',
            render: () => <OperationsHubScreen />,
          },
          {
            id: 'org-chart',
            label: 'Org chart',
            url: 'app.buildersbreakthrough.com/portal/operations/org-chart',
            caption: 'The org chart tracks every role with grade, tenure, and salary. Green checks are current team members and numbered blue circles are planned hires in hiring order.',
            sidebar: 'portal',
            sidebarActive: 'operations',
            render: () => <OrgChartScreen />,
          },
          {
            id: 'sales-materials',
            label: 'Sales materials',
            url: 'app.buildersbreakthrough.com/portal/sales/resources',
            caption: 'Sales materials are ready-made proposals, contracts, scripts, and checklists. Tailor drops in the company name, logo, and terms and turns each one into a branded PDF.',
            sidebar: 'portal',
            sidebarActive: 'sales',
            render: () => <SalesMaterialsScreen />,
          },
          {
            id: 'tech-stack',
            label: 'Tech stack',
            url: 'app.buildersbreakthrough.com/portal/technology/tech-stack',
            caption: 'The tech stack pyramid maps every tool the company runs on, from Core to Secondary. Green rings mark software with an open API, and the Proposed List holds tools we recommend adding.',
            sidebar: 'portal',
            sidebarActive: 'technology',
            render: () => <TechStackScreen />,
          },
          {
            id: 'finance-analytics',
            label: 'Finance analytics',
            url: 'app.buildersbreakthrough.com/portal/finance/analytics',
            caption: 'Finance analytics reads signed orders and costs straight from JobTread: gross margin by month, gross and net profit, receivables against payables, and the overhead number that drives net margin.',
            sidebar: 'portal',
            sidebarActive: 'finance',
            render: () => <FinanceAnalyticsScreen />,
          },
          {
            id: 'marketing-analytics',
            label: 'Marketing analytics',
            url: 'app.buildersbreakthrough.com/portal/marketing/analytics',
            caption: 'Marketing analytics tracks leads by month and where they come from, then works backward from the revenue goal to the number of leads needed at the current close and qualified rates.',
            sidebar: 'portal',
            sidebarActive: 'marketing',
            render: () => <MarketingAnalyticsScreen />,
          },
          {
            id: 'hr-materials',
            label: 'HR materials',
            url: 'app.buildersbreakthrough.com/portal/hr/materials',
            caption: 'HR materials cover hiring, onboarding, and the employee handbook. Tailor fills each one with the company name, logo, and policies and produces a branded PDF.',
            sidebar: 'portal',
            sidebarActive: 'hr',
            render: () => <HRMaterialsScreen />,
          },
          {
            id: 'courses',
            label: 'Courses',
            url: 'app.buildersbreakthrough.com/portal/courses',
            caption: 'The course library is on-demand training for the owner and the team, organized by department with progress tracked per person. New courses and lessons are flagged as they land.',
            sidebar: 'portal',
            sidebarActive: 'courses',
            render: () => <CoursesScreen />,
          },
          {
            id: 'dashboard',
            label: 'Dashboard',
            url: 'app.buildersbreakthrough.com/portal',
            caption: 'Fully unlocked, the dashboard is home base: what is new, implementation hours and upcoming calls, the next Breakthrough Session, tasks, community, roadmap progress, and pinned goals.',
            sidebar: 'portal',
            sidebarActive: 'dashboard',
            render: () => <DashboardScreen firstName={firstName} />,
          },
        ],
      },
    ],
    [firstName]
  );

  return <FlowPreviewShell steps={steps} stepIndex={stepIndex} onStepChange={onStepChange} showStepTabs={false} />;
};

export default ProcessFlowPreview;
